'use es6';

import I18n from 'I18n';
import { UPDATE_POST_CONTENT_MODEL_BLOG_AUTHOR_ID, UPDATE_POST_CONTENT_MODEL_BLOG_AUTHOR, UPDATE_POST_CONTENT_MODEL_TITLE, UPDATE_POST_CONTENT_MODEL_TAG_IDS, UPDATE_POST_CONTENT_MODEL_SLUG, UPDATE_POST_CONTENT_MODEL_CONTENT_GROUP_ID, UPDATE_POST_CONTENT_MODEL_LANGUAGE, UPDATE_POST_CONTENT_MODEL_HEAD_HTML, UPDATE_POST_CONTENT_MODEL_TEMPLATE, UPDATE_POST_CONTENT_MODEL_PUBLIC_ACCESS_RULES, UPDATE_POST_CONTENT_MODEL_PUBLIC_ACCESS_RULES_ENABLED, UPDATE_POST_CONTENT_MODEL_SEO_CRAWLING_ENABLED, UPDATE_TEXT_TO_AUDIO_FILE_ID } from './actionTypes';
import { ADDED_MODULES_TO_POST_BODY, UPDATE_POST_BODY, UPDATE_TITLE_FROM_INLINE } from 'ContentEditorUI/redux/actions/actionTypes';
import { getSlugWithBlogSlug } from '../../utils';
import { getCustomizeTitleEnabled, getPostSettingsBlogs, getBlogPostSettingsSlugRaw } from '../selectors/postSettingsSelectors';
import { makeSpecificBlogSlugForLang, getCurrentBlog } from '../selectors/blogSelectors';
import { fetchPostTags, fetchPostAvailableAuthors } from './postSettingsActions';
import { getContentModelHtmlTitle, getContentModelPostBody } from '../selectors/postContentModelSelectors';
import { getBlogFromGroupForTargetLanguage } from '../../utils';
import { getSchemaForModule } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { getContentSchemaWidgets } from 'ContentEditorUI/redux/selectors/contentSchemaSelectors';
import { getContentModelName } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { addModulesToFakeModules, addCustomUpdatesForFakeModules } from 'ContentEditorUI/redux/actions/moduleActions';

// UPDATES ------------------------------->

export const updateBlogAuthorId = id => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_BLOG_AUTHOR_ID,
    payload: id
  };
};
export const updateBlogAuthor = blogAuthor => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_BLOG_AUTHOR,
    payload: blogAuthor
  };
};
export const updateTitleFromUndoRedo = title => (dispatch, getState) => {
  const customizeTitleEnabled = getCustomizeTitleEnabled(getState());
  dispatch({
    type: UPDATE_POST_CONTENT_MODEL_TITLE,
    payload: {
      title,
      customizeTitleEnabled
    },
    isFakeUndoRedoProxiedUpdate: true
  });
};
export const updateTitle = title => (dispatch, getState) => {
  const customizeTitleEnabled = getCustomizeTitleEnabled(getState());
  dispatch({
    type: UPDATE_POST_CONTENT_MODEL_TITLE,
    payload: {
      title,
      customizeTitleEnabled
    },
    metaData: {
      inlineEdited: false
    },
    undoData: {
      groupKey: 'post_title'
    }
  });
};
export const updatePostBodyFromUndoRedo = postBody => {
  return {
    type: UPDATE_POST_BODY,
    payload: {
      html: postBody
    },
    isFakeUndoRedoProxiedUpdate: true
  };
};
export const updatePostBodyFromXrayPanel = postBody => {
  return {
    type: UPDATE_POST_BODY,
    payload: {
      html: postBody
    },
    metaData: {
      inlineEdited: false
    },
    undoData: {
      splitNext: true
    }
  };
};
export const updateTagIds = ids => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_TAG_IDS,
    payload: ids
  };
};
export const updatePostContentModelSlug = (slug, blogId) => (dispatch, getState) => {
  const state = getState();
  const blogs = getPostSettingsBlogs(state);
  const htmlTitle = getContentModelHtmlTitle(state);
  dispatch({
    type: UPDATE_POST_CONTENT_MODEL_SLUG,
    payload: {
      slugRaw: slug,
      contentModelSlug: getSlugWithBlogSlug(slug, blogs, blogId),
      htmlTitle
    }
  });
};
const updatePostContentModelTemplate = blogId => (dispatch, getState) => {
  const state = getState();
  const postSettingsBlogs = getPostSettingsBlogs(state);
  const selectedBlog = postSettingsBlogs.toJS().find(blog => blog.id === blogId);
  if (!selectedBlog) {
    return;
  }
  const {
    itemTemplatePath
  } = selectedBlog;
  dispatch({
    type: UPDATE_POST_CONTENT_MODEL_TEMPLATE,
    payload: itemTemplatePath
  });
};
export const updateContentGroupId = (blogId, slug) => dispatch => {
  // update the content model slug before updating blog id
  // this way it sends correct slug info to the API.
  dispatch(updatePostContentModelSlug(slug, blogId));
  dispatch({
    type: UPDATE_POST_CONTENT_MODEL_CONTENT_GROUP_ID,
    payload: blogId
  });
  dispatch(updatePostContentModelTemplate(blogId));
};

// Updates contentGroupId and slug to reflect blog change, we do not want to
// save the language field itself, just update it in redux to show in UI
export const updateBlogForLanguage = (language, targetBlog) => (dispatch, getState) => {
  const state = getState();
  const blogInUpdatedLanguage = getBlogFromGroupForTargetLanguage(language, targetBlog || getCurrentBlog(state));
  if (blogInUpdatedLanguage) {
    const blogInUpdatedLanguageId = blogInUpdatedLanguage.get('id');
    dispatch({
      type: UPDATE_POST_CONTENT_MODEL_LANGUAGE,
      payload: {
        language,
        contentGroupId: blogInUpdatedLanguageId,
        slugRaw: getBlogPostSettingsSlugRaw(state),
        blogSlug: makeSpecificBlogSlugForLang(blogInUpdatedLanguageId)(state)
      }
    });
    dispatch(fetchPostTags(language));
    dispatch(fetchPostAvailableAuthors(language));
  }
};
export const updateHeadHtml = value => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_HEAD_HTML,
    payload: value
  };
};
export const updatePublicAccessRules = publicAccessRules => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_PUBLIC_ACCESS_RULES,
    payload: publicAccessRules
  };
};
export const updatePublicAccessRulesEnabled = publicAccessRulesEnabled => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_PUBLIC_ACCESS_RULES_ENABLED,
    payload: publicAccessRulesEnabled
  };
};
export const updateSEOCrawlingEnabled = isCrawlableByBots => {
  return {
    type: UPDATE_POST_CONTENT_MODEL_SEO_CRAWLING_ENABLED,
    payload: isCrawlableByBots
  };
};
export const updateTextToAudioFileId = fileId => {
  return {
    type: UPDATE_TEXT_TO_AUDIO_FILE_ID,
    payload: {
      fileId
    }
  };
};
export const processFakeModulesOffOfContentSchema = () => (dispatch, getState) => {
  let isInCustomModule = false;
  const customModuleParentIds = [];

  // Checking if the post body included as HUBL in a custom module instead of directly in the blog
  // template (coded or D&D), so that we can correctly allow clicking into post_body's nested in custom_modules.
  const reduxState = getState();
  const widgets = getContentSchemaWidgets(reduxState);
  for (const key in widgets) {
    if (Object.hasOwnProperty.call(widgets, key)) {
      const w = widgets[key];
      const moduleSchema = getSchemaForModule(reduxState, w);
      if (moduleSchema && moduleSchema.sourceContainsPostBody) {
        isInCustomModule = true;
        customModuleParentIds.push(key);
      }
    }
  }

  // make fake widgets for the post_body and name
  const postBodyData = getContentModelPostBody(reduxState);
  const postBodyWidget = {
    type: 'rich_text',
    name: 'post_body',
    id: 'post_body',
    __hs_internal: true,
    overrideable: true,
    label: I18n.text('modules.post_body'),
    isInCustomModule,
    customModuleParentIds,
    body: {
      html: postBodyData
    }
  };
  const postBodyWidgetUpdate = {
    moduleName: 'post_body',
    updatePath: ['html']
  };
  const postNameWidget = {
    type: 'text',
    name: 'name',
    id: 'name',
    __hs_internal: true,
    label: I18n.text('modules.name'),
    body: {
      title: getContentModelName(reduxState)
    }
  };
  const postNameWidgetUpdate = {
    moduleName: 'name',
    updatePath: []
  };
  dispatch(addModulesToFakeModules({
    name: postNameWidget,
    post_body: postBodyWidget
  }));
  const customUpdates = {};
  customUpdates[UPDATE_POST_CONTENT_MODEL_TITLE] = postNameWidgetUpdate;
  customUpdates[UPDATE_TITLE_FROM_INLINE] = postNameWidgetUpdate;
  customUpdates[UPDATE_POST_BODY] = postBodyWidgetUpdate;
  customUpdates[ADDED_MODULES_TO_POST_BODY] = postBodyWidgetUpdate;
  dispatch(addCustomUpdatesForFakeModules(customUpdates));
};